const DEFAULT_MAX_RETRIES = 1;
const DEFAULT_RETRY_DELAY_MS = 200;

type FetchLike = typeof fetch;
type FetchInput = Parameters<FetchLike>[0];
type FetchInit = Parameters<FetchLike>[1];

export type SupabaseReadRetryOptions = {
  maxRetries?: number;
  retryDelayMs?: number;
  sleep?: (delayMs: number) => Promise<void>;
};

function requestMethod(input: FetchInput, init?: FetchInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return 'GET';
}

function isSafeReadMethod(method: string): boolean {
  return method === 'GET' || method === 'HEAD';
}

function isRetryableResponse(response: Response): boolean {
  return [502, 503, 504].includes(response.status);
}

function isRetryableThrownError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (error instanceof Error && error.name === 'AbortError') return false;
  return true;
}

/**
 * Adds one short retry to idempotent Supabase browser reads.
 *
 * Mutations are never replayed. Long-running 522 responses are also not
 * replayed because a second origin timeout would make the recovery experience
 * worse; the caller's existing error state remains authoritative in that case.
 */
export function createSupabaseReadRetryFetch(
  baseFetch: FetchLike = globalThis.fetch.bind(globalThis),
  options: SupabaseReadRetryOptions = {},
): FetchLike {
  const maxRetries = Math.max(0, options.maxRetries ?? DEFAULT_MAX_RETRIES);
  const retryDelayMs = Math.max(0, options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS);
  const sleep =
    options.sleep ?? ((delayMs: number) => new Promise((resolve) => setTimeout(resolve, delayMs)));

  return (async (input: FetchInput, init?: FetchInit) => {
    const method = requestMethod(input, init);
    if (!isSafeReadMethod(method) || maxRetries === 0) {
      return baseFetch(input, init);
    }

    for (let attempt = 0; ; attempt += 1) {
      try {
        const response = await baseFetch(input, init);
        if (attempt >= maxRetries || !isRetryableResponse(response)) return response;
      } catch (error) {
        if (attempt >= maxRetries || !isRetryableThrownError(error)) throw error;
      }

      await sleep(retryDelayMs * 2 ** attempt);
    }
  }) as FetchLike;
}
